/**
 * Small helpers for composing style objects while preserving the
 * modular "styles live in a sibling *.styles.ts" contract.
 *
 * `mergeSx` lets a component conditionally combine style entries
 * without leaking the branching into an ad-hoc inline `sx` in the JSX.
 */
import type { SxProps, Theme } from '@mui/material/styles';

type SxInput = SxProps<Theme> | false | null | undefined;

/**
 * Flatten a list of `SxProps<Theme>` (or falsy values) into a single
 * `SxProps<Theme>` array that MUI's `sx` prop accepts. Falsy entries
 * are dropped so callers can gate additions with a boolean.
 *
 * Example:
 *   sx={mergeSx(styles.dot, isActive && styles.dotActive)}
 */
export function mergeSx(...entries: SxInput[]): SxProps<Theme> {
  const flat: SxProps<Theme>[] = [];
  for (const entry of entries) {
    if (!entry) continue;
    if (Array.isArray(entry)) {
      for (const inner of entry) {
        if (inner) flat.push(inner as SxProps<Theme>);
      }
    } else {
      flat.push(entry);
    }
  }
  return flat as unknown as SxProps<Theme>;
}
